/**
 * MissingDocumentsTable
 *
 * Missing documents from supervisees. UnifyTable with fixed layout.
 */

import { Anchor, Badge, Group, Text } from '@mantine/core';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, UnifyTableSortAffordance, type UnifyTableColumn } from '@/components/UnifyTable';
import type { MissingDocumentItem, MissingDocumentFilters } from '../types';
import {
  MissingDocumentStatusLabels,
  canNavigateMissingDocumentRow,
  getMissingDocumentRowTypeLabel,
} from '../types';

type SortColumn = MissingDocumentFilters['sortBy'];

const statusColors: Record<string, string> = {
  overdue: 'red',
  'due-soon': 'orange',
  missing: 'yellow',
  unsigned: 'blue',
  'edits-requested': 'violet',
};

interface MissingDocumentsTableProps {
  items: MissingDocumentItem[];
  sortBy: SortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: SortColumn) => void;
  onRowClick?: (item: MissingDocumentItem) => void;
}

export function MissingDocumentsTable({
  items,
  sortBy,
  sortDirection,
  onSort,
  onRowClick,
}: MissingDocumentsTableProps): JSX.Element {
  const formatDate = (d: string | undefined) =>
    d ? new Date(d).toLocaleDateString('en-US') : '—';

  const columns = useMemo((): UnifyTableColumn<MissingDocumentItem>[] => {
    const hdr = (label: string, column: SortColumn) => {
      const isActive = sortBy === column;
      return (
        <Group
          gap={6}
          wrap="nowrap"
          onClick={() => onSort(column)}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <Text component="span" size="sm" fw={600}>
            {label}
          </Text>
          <UnifyTableSortAffordance active={isActive} direction={sortDirection} />
        </Group>
      );
    };

    return [
      {
        key: 'client',
        label: 'Client',
        renderHeader: () => hdr('Client', 'client'),
        render: (item) =>
          item.clientId ? (
            <Anchor component={Link} to={`/dashboard/patients/${item.clientId}`} size="sm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
              {item.clientName ?? '—'}
            </Anchor>
          ) : (
            <Text size="sm">{item.clientName ?? '—'}</Text>
          ),
        getValue: (item) => item.clientName ?? '',
      },
      {
        key: 'sessionDate',
        label: 'Session Date',
        renderHeader: () => hdr('Session Date', 'sessionDate'),
        render: (item) => <Text size="sm">{formatDate(item.sessionDate)}</Text>,
        getValue: (item) => item.sessionDate ?? '',
      },
      {
        key: 'dueDate',
        label: 'Due Date',
        renderHeader: () => hdr('Due Date', 'dueDate'),
        render: (item) => (
          <Text size="sm" c={item.status === 'overdue' ? 'red' : undefined}>
            {formatDate(item.dueDate)}
          </Text>
        ),
        getValue: (item) => item.dueDate ?? '',
      },
      {
        key: 'clinician',
        label: 'Provider',
        renderHeader: () => hdr('Provider', 'clinician'),
        render: (item) => <Text size="sm">{item.clinicianName}</Text>,
        getValue: (item) => item.clinicianName,
      },
      {
        key: 'type',
        label: 'Type',
        renderHeader: () => hdr('Type', 'type'),
        render: (item) => (
          <Text size="sm">{getMissingDocumentRowTypeLabel(item.category, item.formType)}</Text>
        ),
        getValue: (item) => getMissingDocumentRowTypeLabel(item.category, item.formType),
      },
      {
        key: 'status',
        label: 'Status',
        renderHeader: () => hdr('Status', 'status'),
        render: (item) => (
          <Badge size="sm" variant="light" color={statusColors[item.status] ?? 'gray'}>
            {MissingDocumentStatusLabels[item.status]}
          </Badge>
        ),
        getValue: (item) => MissingDocumentStatusLabels[item.status],
      },
    ];
  }, [onSort, sortBy, sortDirection]);

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={items}
      getRowKey={(item) => item.id}
      onRowClick={
        onRowClick
          ? (item) => {
              if (canNavigateMissingDocumentRow(item)) {
                onRowClick(item);
              }
            }
          : undefined
      }
      tableProps={{ style: { width: '100%', tableLayout: 'fixed', minWidth: 800 } }}
    />
  );
}
